import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { KoobButton, KoobModal } from '@koob/ui';

export interface FolderArchiveModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  folderName?: string;
  onConfirm: () => Promise<void> | void;
}

const emphasis = { strong: <strong className="font-bold text-k-h" /> };

/** KD-3842: confirmation before archiving a client folder. */
export default function FolderArchiveModal({
  open,
  setOpen,
  folderName,
  onConfirm,
}: FolderArchiveModalProps) {
  const { t } = useTranslation('folders');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <KoobModal
      open={open}
      setOpen={setOpen}
      widthClass="sm:max-w-[480px]"
      paddingClass="p-0"
    >
      <div className="flex flex-col gap-2.5 px-[22px] pb-1.5 pt-[22px]">
        <h2 className="m-0 text-[17px] font-bold text-k-h">
          {t('archive.title')}
        </h2>
        <p className="m-0 text-[13.5px] leading-normal text-gray-600">
          <Trans
            t={t}
            i18nKey="archive.description"
            values={{ name: folderName }}
            components={emphasis}
          />
        </p>
        <p className="m-0 rounded-[10px] bg-gray-100 px-3.5 py-3 text-[12.5px] leading-normal text-gray-600">
          <Trans t={t} i18nKey="archive.help" components={emphasis} />
        </p>
      </div>

      <div className="mt-3 flex justify-end gap-2.5 border-0 border-t border-solid border-gray-100 bg-gray-50 px-[22px] py-[18px]">
        <KoobButton
          variant="secondary"
          onClick={() => setOpen(false)}
          data-testid="archive-folder-cancel"
        >
          {t('misc:cancel')}
        </KoobButton>
        <KoobButton
          variant="navy"
          onClick={handleConfirm}
          disabled={isSubmitting}
          data-testid="archive-folder-confirm"
        >
          {t('archive.confirm')}
        </KoobButton>
      </div>
    </KoobModal>
  );
}
